import { useCallback, useEffect, useState } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { Loading } from "src/components";
import { Button } from "src/components/ui/button";
import { crashedRightAfterCacheWipe, wipeCacheAndReloadOnce } from "./CacheErrorBoundary";

/**
 * Root route `errorElement`. React Router catches route render errors itself, so they never reach
 * `CacheErrorBoundary` — without this a crash caused by stale persisted data left the user on the
 * router's "Unexpected Application Error!" screen. Wipes the cache and reloads once; a crash right
 * after that reload is real and is shown.
 */
export const RouteCrashRecovery = () => {
  const error = useRouteError();
  // Decided once per mount so StrictMode's second effect run can't flip the reload into the fallback.
  const [reloading] = useState(() => !isRouteErrorResponse(error) && !crashedRightAfterCacheWipe());

  useEffect(() => {
    if (reloading) {
      wipeCacheAndReloadOnce(error);
    }
  }, [reloading, error]);

  if (reloading) {
    return <Loading name="RouteCrashRecovery" />;
  }
  return <RouteCrashDetails error={error} />;
};

const RouteCrashDetails = ({ error }: { error: unknown }) => {
  const reload = useCallback(() => window.location.reload(), []);
  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : String(error);

  return (
    <div className="flex flex-col gap-y-3 p-6">
      <p className="text-lg font-semibold text-foreground">Unexpected application error</p>
      <p className="italic text-muted-foreground">{message}</p>
      {error instanceof Error && error.stack ? (
        <pre className="overflow-x-auto rounded bg-muted p-3 text-xs text-foreground">{error.stack}</pre>
      ) : null}
      <Button className="self-start" onClick={reload}>
        Reload
      </Button>
    </div>
  );
};
